import { spawn } from "node:child_process";
import { closeSync, openSync, readFileSync } from "node:fs";
import * as os from "node:os";
import * as path from "node:path";
import { readBootstrapState } from "./bootstrap-state.js";
import { readStatusBlob } from "./bootstrap-status.js";
import { getFoundryToken } from "./auth.js";
import { LocalCliError } from "./errors.js";
import { colors } from "./output.js";
import {
  findOnboardingProfile, renderCompanyProfile, companyProfileBodyEquals, upsertMemoryIndex,
  DEFAULT_MEMORY_INDEX_HEADER, COMPANY_PROFILE_PATH, type OnboardingBlock,
} from "./onboarding-profile.js";
import { mintInstallationTokenFromPem, readRepoFileViaApp, commitFilesViaApp } from "./github-app-repo.js";

export interface GithubAppCreds { appId: string; slug: string; org: string; installationId: string; pemPath: string }

export function readGithubAppCreds(credsPath?: string): GithubAppCreds | null {
  const p = credsPath ?? path.join(os.homedir(), ".m8t-stack", "github-app.json");
  // malformed or absent → null (best-effort seed; `launch` already validated these creds for a real install).
  try { return JSON.parse(readFileSync(p, "utf8")) as GithubAppCreds; } catch { return null; }
}

export interface SeedContext { endpoint: string; brainRepo: string; appCreds: GithubAppCreds }

export async function resolveSeedContext(opts: {
  endpointOverride?: string; brainOverride?: string; credsPath?: string; appCredsOverride?: GithubAppCreds;
}): Promise<SeedContext | null> {
  const appCreds = opts.appCredsOverride ?? readGithubAppCreds(opts.credsPath);
  if (!appCreds) return null; // no brains were deployed
  let endpoint = opts.endpointOverride;
  if (!endpoint) {
    const state = await readBootstrapState();
    if (!state) {
      throw new LocalCliError({ code: "BOOTSTRAP_NO_STATE", message: "No bootstrap state found.", hint: "Run 'm8t bootstrap launch' first, or pass --endpoint." });
    }
    const doc = await readStatusBlob({ saName: state.statusSaName, resourceGroup: state.resourceGroup, subscriptionId: state.subscriptionId });
    endpoint = doc.result?.foundryEndpoint;
    if (!endpoint) {
      throw new LocalCliError({ code: "SEED_NO_ENDPOINT", message: "Could not resolve the Foundry endpoint from the install status.", hint: "Pass --endpoint <foundryEndpoint>." });
    }
  }
  return { endpoint, brainRepo: opts.brainOverride ?? `${appCreds.org}/stacey-brain`, appCreds };
}

export async function applyProfileToBrain(args: {
  block: OnboardingBlock; brainRepo: string; branch: string; appCreds: GithubAppCreds; now?: string; fetchImpl?: typeof fetch;
}): Promise<void> {
  const token = await mintInstallationTokenFromPem({
    appId: args.appCreds.appId,
    privateKeyPem: readFileSync(args.appCreds.pemPath, "utf8"),
    installationId: args.appCreds.installationId,
    fetchImpl: args.fetchImpl,
  });
  const { profileMd, memoryIndexLine } = renderCompanyProfile(args.block, args.now);
  const existingProfile = await readRepoFileViaApp({ token, repo: args.brainRepo, path: COMPANY_PROFILE_PATH, fetchImpl: args.fetchImpl });
  const existingIndex =
    (await readRepoFileViaApp({ token, repo: args.brainRepo, path: "memory/MEMORY.md", fetchImpl: args.fetchImpl }))
    ?? DEFAULT_MEMORY_INDEX_HEADER;
  const nextIndex = upsertMemoryIndex(existingIndex, memoryIndexLine);
  if (existingProfile && companyProfileBodyEquals(existingProfile, profileMd) && nextIndex === existingIndex) {
    return; // unchanged — true no-op (no commit churn from a regenerated timestamp)
  }
  await commitFilesViaApp({
    token, repo: args.brainRepo, branch: args.branch,
    message: "seed(brain): company profile from onboarding questionnaire",
    files: [
      { path: COMPANY_PROFILE_PATH, content: profileMd },
      { path: "memory/MEMORY.md", content: nextIndex },
    ],
    fetchImpl: args.fetchImpl,
  });
}

/** Spawn a detached `m8t bootstrap seed-profile --watch` that survives this process. */
export function spawnDetachedSeedWatch(): void {
  const logPath = path.join(os.homedir(), ".m8t-stack", "seed-profile.log");
  const fd = openSync(logPath, "a");
  try {
    const child = spawn(process.execPath, [process.argv[1] ?? "", "bootstrap", "seed-profile", "--watch"], {
      detached: true, stdio: ["ignore", fd, fd],
    });
    child.unref();
  } finally {
    closeSync(fd);
  }
}

/** Reactive seed at `bootstrap finish`: seed inline if ready; else background-watch; else (skipped) no-op. */
export async function reactiveSeedOnFinish(args: {
  endpoint?: string; stdout: (s: string) => void;
  getFoundryTokenImpl?: () => Promise<string>; spawnWatch?: () => void;
  fetchImpl?: typeof fetch; appCredsOverride?: GithubAppCreds;
}): Promise<void> {
  const ctx = await resolveSeedContext({ endpointOverride: args.endpoint, appCredsOverride: args.appCredsOverride });
  if (!ctx) return; // no GitHub App creds → no brains → nothing to seed
  const token = await (args.getFoundryTokenImpl ?? getFoundryToken)();
  const { hadIntake, block } = await findOnboardingProfile({ endpoint: ctx.endpoint, token, fetchImpl: args.fetchImpl });
  if (block) {
    await applyProfileToBrain({ block, brainRepo: ctx.brainRepo, branch: "main", appCreds: ctx.appCreds, fetchImpl: args.fetchImpl });
    args.stdout(`${colors.success("✓")} Clever Stacey now knows your company (seeded ${ctx.brainRepo}).\n`);
    return;
  }
  if (!hadIntake) return; // founder skipped the questionnaire — nothing to wait for
  (args.spawnWatch ?? spawnDetachedSeedWatch)();
  args.stdout(
    `${colors.dim("ℹ Stacey will pick up your company profile when you finish the questionnaire (watching in the background).")}\n` +
    `  ${colors.hint("or run:")} m8t bootstrap seed-profile\n`,
  );
}
